import { Link } from "react-router-dom";
import type { GossipItem } from "@/mocks/gossip";

const typeMeta: Record<GossipItem["type"], { icon: string; label: string }> = {
  video: { icon: "ri-play-circle-fill", label: "视频" },
  image: { icon: "ri-image-2-fill", label: "图集" },
  text: { icon: "ri-article-fill", label: "速览" },
};

interface GossipCardProps {
  item: GossipItem;
  variant?: "vertical" | "horizontal";
  showSummary?: boolean;
}

export default function GossipCard({ item, variant = "vertical", showSummary = true }: GossipCardProps) {
  if (variant === "horizontal") {
    return (
      <article className="group flex gap-4 bg-background-50 border border-background-200 rounded-lg p-3 hover:border-primary-300 transition-colors">
        <Link
          to={`/article/${item.id}`}
          className="relative w-28 sm:w-36 shrink-0 aspect-[4/3] rounded-md overflow-hidden bg-background-200"
        >
          <img
            src={item.cover}
            alt={`${item.title} 画面预览`}
            title={`${item.title} - 网曝吃瓜画面`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </Link>
        <div className="flex flex-col min-w-0 py-0.5">
          <Link to={`/category/${item.categorySlug}`} className="text-xs text-primary-600 font-medium mb-1.5">
            {item.category}
          </Link>
          <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 leading-snug line-clamp-2">
            <Link to={`/article/${item.id}`} className="hover:text-primary-700 transition-colors">
              {item.title}
            </Link>
          </h3>
          <p className="hidden sm:block mt-2 text-xs text-foreground-600 leading-relaxed line-clamp-2">
            {item.summary}
          </p>
          <div className="mt-auto pt-3 flex items-center gap-3 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1 text-primary-600">
              <i className="ri-fire-line" aria-hidden="true"></i>
              {item.heat}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-eye-line" aria-hidden="true"></i>
              {item.views}
            </span>
            <span className="hidden sm:inline-flex items-center gap-1">
              <i className="ri-chat-3-line" aria-hidden="true"></i>
              {item.comments}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors">
      <Link to={`/article/${item.id}`} className="relative block w-full aspect-[16/10] overflow-hidden bg-background-200">
        <img
          src={item.cover}
          alt={`${item.title} 画面预览`}
          title={`${item.title} - 网曝吃瓜画面`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-foreground-950/75 text-background-50 text-[11px] font-label backdrop-blur-sm">
          <i className={typeMeta[item.type].icon} aria-hidden="true"></i>
          {typeMeta[item.type].label}
        </span>
      </Link>
      <div className="flex flex-col flex-1 p-4 md:p-5">
        <Link to={`/category/${item.categorySlug}`} className="text-xs text-primary-600 font-medium mb-2">
          {item.category}
        </Link>
        <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 leading-snug line-clamp-2">
          <Link to={`/article/${item.id}`} className="hover:text-primary-700 transition-colors">
            {item.title}
          </Link>
        </h3>
        {showSummary && (
          <p className="mt-2.5 text-sm text-foreground-600 leading-relaxed line-clamp-2">{item.summary}</p>
        )}
        <div className="mt-auto pt-4 flex items-center gap-3 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1 text-primary-600">
            <i className="ri-fire-line" aria-hidden="true"></i>
            热度 {item.heat}
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-eye-line" aria-hidden="true"></i>
            {item.views}
          </span>
          <span className="ml-auto text-foreground-400">{item.date}</span>
        </div>
      </div>
    </article>
  );
}